import React from "react"

// Importing localization utility
import { localizer } from "../../../../Translator.js";

// Importing custom styles
import '../../../../custom.css'

// Material-UI components for layout and styling
import Grid from '@material-ui/core/Grid'
import Container from '@material-ui/core/Container'
import { TextField } from "@material-ui/core"
import { Paper } from "@material-ui/core"
import { Checkbox } from "@material-ui/core"
import { FormControlLabel } from "@material-ui/core"
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Alert from '@material-ui/lab/Alert'
import { Backdrop, CircularProgress } from "@material-ui/core"
import { TableContainer, TableHead, TableBody } from "@material-ui/core"
import Typography from '@material-ui/core/Typography'
import { Button } from  "@material-ui/core"

// Importing custom components
import { SummaryRow } from "./SummaryRow.js";
import { TableStyled, TableCellStyled } from '../../../common/TableStyled.js'
import { TextFieldStyled } from "../../../common/InputStyled.js"
import { TableRowHeaderStyled } from '../../../common/TableStyled.js'
import ScreenHeading from '../../../common/ScreenHeading.js';
import { SnackbarCustom } from "../../../Utilities/SnackbarCustom.js";
import DateCustom from "../../../Utilities/DateCustom.js";

// Importing SDK clients for API calls
import OutboxCli from "../../../../sdk/OutboxCli.js"
import UserCli from "../../../../sdk/UserCli.js"

const BATCH_LABEL_MAX_LENGTH = 256

/**
 * Panel containing a summary of the shipment being edited and about to be emitted/saved/scheduled.
 */
export class OutboxSummary extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      // Represents global shipment list given by parent component to be updated
      shipments: [],

      // Attributes to determinate when a dialog is open/closed.
      openModal: false,

      // Success or error message on failure trying to save a shipment.
      storeShipmentError: null,

      // Waiting for server response indicator.
      isLoading: false,

      // Optional grouping of shipments in a batch.
      enableBatchGrouping: false,
      batchLabel: "",
    }
  }

  /**
   * Lifecycle method: Initializes the component with shipment data passed from the parent.
   */
  componentDidMount() {
    this.setState({ shipments: this.props.shipments })
  }

  /**
   * Handles navigation between steps in the parent component.
   * @param {Object} event - The event triggered by the step button.
   */
  handleStep = (event) => {
    var direction = event.currentTarget.name

    this.props.handleOutboxState(this.state.shipments, null, 5, direction, null)
  }

  /**
   * Opens the modal dialog for scheduling a shipment.
   */
  handleOpenModal = () => {
    this.setState({ openModal: true })
  }

  /**
   * Closes the modal dialog for scheduling a shipment.
   */
  handleCloseModal = () => {
    this.setState({ openModal: false })
  }

  /**
   * Updates the shipment emission date in the state when the user selects a new date.
   * @param {Object} event - The event triggered by the date picker.
   */
  handleDateChange = (event) => {
    var date = event.currentTarget.value
    // Convert selected date (in local time zone) to UTC (as required for storage).
    var dateUtc = DateCustom.convertToUtcDateTime(date)
    
    let shipments = this.state.shipments.map(s => {
      s.emissionTime = dateUtc
      return s
    })
    this.setState({ shipments: shipments })
  }

  /**
   * Handles navigation after storing shipment data.
   * @param {string} action - The action performed (e.g., SEND, SCHEDULE, STORE).
   */
  handleStepAfterStore = (action) => {
    this.props.handleOutboxState(this.state.shipments, null, 5, "next", action)
  }

  /**
   * Calls the API to store the shipment data based on the selected action.
   * @param {Object} event - The event triggered by the action button.
   */
  storeShipment = async (event) => {
    if (this.state.enableBatchGrouping && !this.state.batchLabel.trim()) {
      this.setState({ storeShipmentError: localizer.BatchLabelRequired })
      return
    }

    if (this.state.enableBatchGrouping && this.state.batchLabel.trim().length > BATCH_LABEL_MAX_LENGTH) {
      const maxLengthMessage = localizer.BatchLabelTooLong || `Batch label cannot exceed ${BATCH_LABEL_MAX_LENGTH} characters.`
      this.setState({ storeShipmentError: maxLengthMessage })
      return
    }

    this.setState({ openModal: false, isLoading: true })
    
    let action = event.currentTarget.name
    var emissionTime = null
    let shipments = []
    switch (action.toUpperCase()) {
      case 'SEND':
        // Set the current date/time as the emission time for sending shipments.
        emissionTime = DateCustom.convertToUtcDateTime()
        shipments = this.state.shipments.map(s => {
          s.emissionTime = emissionTime
          return s
        })
        break;
      case 'SCHEDULE':
        // Use the existing shipment data for scheduling.
        shipments = this.state.shipments
        break;
      case 'STORE':
        // Store shipments with the current emission time.
        shipments = this.state.shipments.map(s => {
          s.emissionTime = emissionTime
          return s
        })
        break;
    
      default:
        break;
    }

    this.setState({ isLoading: true, openModal: false, shipments: shipments }, async () => {
      // Get e-mail address to be used for reception of shipment emission confirmation.
      await UserCli.GetShipmentEmissionEmail().then(async emissionEmail => {
        if (emissionEmail !== null && emissionEmail.toString().startsWith("[ERR")) {
          emissionEmail = ""
        }

        // Batch API call to store shipments
        const batchLabel = this.state.enableBatchGrouping ? this.state.batchLabel.trim() : null;
        let response = await OutboxCli.StoreShipments(this.state.shipments, emissionEmail, true, batchLabel);

        if (typeof response === "string") {
          const cleanedMessage = response.replace(/\[ERR_OUTBOX[^\]]*\]\s*/i, "");
          this.setState({ storeShipmentError: cleanedMessage || response, isLoading: false });
          return;
        }

        // Normalize response to array if it's a single object
        if (response && !Array.isArray(response)) {
          response = [response];
        }

        // Normalize backend naming policy differences (camelCase vs PascalCase)
        if (Array.isArray(response)) {
          response = response.map(r => ({
            ...r,
            success: (r && r.success !== undefined && r.success !== null) ? r.success : (r ? r.Success : undefined),
            errorMessage: (r && r.errorMessage !== undefined && r.errorMessage !== null) ? r.errorMessage : (r ? r.ErrorMessage : undefined),
            shipment: (r && r.shipment !== undefined && r.shipment !== null) ? r.shipment : (r ? r.Shipment : undefined),
          }));
        }

        // Handle error responses (BadRequest) with detailed error messages
        if (response && Array.isArray(response) && response.length > 0 && response.every(r => r.success === false)) {
          // Clean error message prefix [ERR_OUTBOX_*] if present
          let errormessage = response.map(r => {
            if (!r.errorMessage) return "";
            return r.errorMessage.replace(/\[ERR_OUTBOX[^\]]*\]\s*/i, "");
          }).join("\n");
          this.setState({ storeShipmentError: errormessage, isLoading: false });
        } else if (response && Array.isArray(response) && response.every(r => r.success === true)) {
          // All shipments stored successfully
          this.setState({ shipments: response.map(r => r.shipment) }, () => { this.handleStepAfterStore(action) });
          this.setState({ isLoading: false });
        } else {
          // Unexpected error
          this.setState({ storeShipmentError: localizer.UnexpectedBatchSaveError || "Unexpected error during batch save.", isLoading: false });
        }
      });
    });
  }

  /**
   * Removes shipments by calling the API for each shipment ID.
   * @param {Array} shipments - The list of shipments to be removed.
   */
  /*removeShipments = (shipments) => {
    shipments.forEach(shipment => {
      OutboxCli.RemoveShipment(shipment.shipmentId, false, false).then(data => {
        if (data !== null && data.toString().startsWith("[ERR")) {
          this.setState({ storeShipmentError: data.substring(data.indexOf(' ')) })
        }
      })
    });
  }*/

  /**
   * Closes the error snackbar.
   */
  handleonClose = () => {
    this.setState({ storeShipmentError: null })
  }

  handleBatchGroupingChange = (event) => {
    const enableBatchGrouping = event.target.checked
    this.setState({
      enableBatchGrouping,
      batchLabel: enableBatchGrouping ? this.state.batchLabel : "",
    })
  }

  handleBatchLabelChange = (event) => {
    this.setState({ batchLabel: event.target.value })
  }

  render() {
    const batchLabelTrimmed = this.state.batchLabel.trim()
    const isBatchLabelTooLong = this.state.enableBatchGrouping && batchLabelTrimmed.length > BATCH_LABEL_MAX_LENGTH
    const batchLabelHelperText = isBatchLabelTooLong
      ? (localizer.BatchLabelTooLong || `Batch label cannot exceed ${BATCH_LABEL_MAX_LENGTH} characters.`)
      : ""

    const emissionTime = this.state.shipments.length >= 1 ?
      this.state.shipments[0].emissionTime !== null ? DateCustom.convertUTCToLocalTimeShort(this.state.shipments[0].emissionTime) :
        DateCustom.convertUTCToLocalTimeShort() : DateCustom.convertUTCToLocalTimeShort()
    return (
      <React.Fragment>
        <Container className="pt-20">
          {/* Screen heading with localized title */}
          <ScreenHeading className="typography-generic" icon={<i className="fa-solid fa-paper-plane icon-button-header mr-20 ml-20"></i>} title={localizer.Send} />
          <Grid md={12}>
            <Alert severity="info" >
              <Typography className="typography-generic color-info" variant="caption" display="inline" gutterBottom>{localizer.ShipmentSummaryHeaderText}</Typography>
            </Alert>
          </Grid>
         
          <Grid item md={12} className="pt-20">
            <Alert severity="info" >
              <Typography className="typography-generic font-weight-bold color-info" variant="caption" display="inline" gutterBottom>{localizer.PreparedShipments}{this.props.shipments.length}</Typography>
            </Alert>

            <TableContainer component={Paper}>
              <TableStyled>
                <TableHead>
                  <TableRowHeaderStyled>
                    <TableCellStyled />
                    <TableCellStyled><Typography className="typography-generic tablerow-header">{localizer.Shipment}</Typography></TableCellStyled>
                    <TableCellStyled ><Typography className="typography-generic tablerow-header">{localizer.ShipmentSumaryRefence}</Typography></TableCellStyled>
                    <TableCellStyled ><Typography className="typography-generic tablerow-header">{localizer.ShipmentSummarySender}</Typography></TableCellStyled>
                    <TableCellStyled ><Typography className="typography-generic tablerow-header">{localizer.ShipmentSummaryType}</Typography></TableCellStyled>
                    <TableCellStyled ><Typography className="typography-generic tablerow-header">{localizer.ShipmentSummaryAddressee}</Typography></TableCellStyled>
                    <TableCellStyled ><Typography className="typography-generic tablerow-header">{localizer.View}</Typography></TableCellStyled>
                  </TableRowHeaderStyled>
                </TableHead>
                <TableBody>
                  {this.state.shipments && this.state.shipments.map((shipment, index) => (
                    <SummaryRow key={index} index={index} shipment={shipment} products={this.props.products} draftsSelected={this.props.draftsSelected} />
                  ))}
                </TableBody>
              </TableStyled>
            </TableContainer>
          </Grid>

          {!this.props.notNewShipment && this.state.shipments.length > 1 &&
            <Grid item md={12} className="pt-20">
              <Alert severity="info">
                <Typography className="typography-generic color-info" variant="caption" display="block" gutterBottom>
                  {localizer.BatchGroupingHint}
                </Typography>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={this.state.enableBatchGrouping} onChange={this.handleBatchGroupingChange} />}
                  label={<Typography className="typography-generic">{localizer.BatchGroupingCheckLabel}</Typography>}
                />
                {this.state.enableBatchGrouping &&
                  <TextFieldStyled
                    label={<Typography className="typography-generic">{localizer.BatchLabelField}</Typography>}
                    value={this.state.batchLabel}
                    onChange={this.handleBatchLabelChange}
                    error={isBatchLabelTooLong}
                    helperText={batchLabelHelperText}
                    variant="outlined"
                    margin="dense"
                    inputProps={{ maxLength: BATCH_LABEL_MAX_LENGTH }}
                    InputProps={{
                      classes: {
                        input: "typography-generic"
                      }
                    }}
                    fullWidth
                  />
                }
              </Alert>
            </Grid>
          }

          {/* Step buttons */}
          <Grid container direction="row" className="pt-20">
            <Grid item md={5} >
              <Button className="button-generic secondary" variant="contained" name="previous" onClick={this.handleStep}>{localizer.Previous}</Button>
            </Grid>
            <Grid item md={7}>
              <Button className="button-generic primary ml-10" variant="contained" name="SEND" onClick={this.storeShipment}>{localizer.Send}</Button>
              <Button className="button-generic primary ml-10" variant="contained" name="SCHEDULE" onClick={this.handleOpenModal}>{localizer.Schedule}</Button>
              <Button className="button-generic primary" variant="contained" name="STORE" onClick={this.storeShipment}>{localizer.Save}</Button>
            </Grid>
          </Grid>
          
          <Dialog
            open={this.state.openModal}
            onClose={this.handleCloseModal}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="md"
          >
            <DialogTitle id="alert-dialog-title">
              <Typography className="typography-generic dialog-title">{localizer.ScheduleShipmentHeaderText}</Typography>
              <i className="fa-solid fa-circle-xmark closeoutlinedicon-custom" onClick={this.handleCloseModal}></i>
            </DialogTitle>
            <DialogContent dividers={true}>
              <Grid container direction="row" spacing={2}>
                <Grid item md={12}>
                  <TextField
                    id="datetime-local"
                    label={<Typography className="typography-generic">{localizer.NextAppointment}</Typography>}
                    type="datetime-local"
                    defaultValue={this.state.shipments.length >= 1 ? emissionTime : ""}
                    onChange={this.handleDateChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      classes: {
                        input: "typography-generic"
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item md={12}>
                  <Button className="button-generic primary" variant="contained" name="SCHEDULE" onClick={this.storeShipment} style={{ marginTop: '20px', marginLeft: '50%' }}>{localizer.Schedule}</Button>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>

          {/* Upload draft error. */}
          <SnackbarCustom message={this.state.storeShipmentError} severity="error" handleonClose={this.handleonClose} />

          {/* Waiting for service response. */}
          {this.state.isLoading &&
            <Backdrop open={this.state.isLoading} style={{ zIndex: 100 }}>
              <CircularProgress color="secondary" />
            </Backdrop>
          }
        </Container>
      </React.Fragment>
    )
  }
}


// WEBPACK FOOTER //
// ./src/components/shipments/sections/summary/OutboxSummary.js